import { useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { cn } from '@helios/ui';
import { settingsSectionLabel, type SettingsNavGroup } from '@/navigation/registry';
import { SettingsField, settingsControlClass } from './settingsChrome';

function settingsDestinationForPath(groups: readonly SettingsNavGroup[], pathname: string) {
  const path = pathname.replace(/\/+$/, '') || '/';
  const items = groups.flatMap((group) => group.items);
  return (
    items.find((item) => item.path === path) ??
    items
      .filter((item) => item.id !== 'settings' && path.startsWith(`${item.path}/`))
      .sort((a, b) => b.path.length - a.path.length)[0]
  );
}

export function SettingsNav({
  groups,
  variant,
}: {
  groups: SettingsNavGroup[];
  variant: 'rail' | 'mobile';
}) {
  const rail = variant === 'rail';
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const navRef = useRef<HTMLElement>(null);
  const current = settingsDestinationForPath(groups, pathname);

  useEffect(() => {
    if (rail) {
      navRef.current
        ?.querySelector<HTMLElement>('[aria-current="page"]')
        ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [rail, current?.id]);

  if (!rail) {
    return (
      <nav aria-label="Settings sections" className="border-border border-b pb-2">
        <SettingsField label="Settings section">
          <select
            className={settingsControlClass}
            value={current?.id ?? ''}
            onChange={(event) => {
              const destination = groups
                .flatMap((group) => group.items)
                .find((item) => item.id === event.target.value);
              if (destination) navigate(destination.path);
            }}
          >
            <option value="" disabled>
              Choose a Settings section
            </option>
            {groups.map((group) => (
              <optgroup key={group.id} label={group.label}>
                {group.items.map((item) => (
                  <option key={item.id} value={item.id}>
                    {settingsSectionLabel(item)}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </SettingsField>
      </nav>
    );
  }

  return (
    <nav ref={navRef} aria-label="Settings sections" className="px-2 py-3">
      <p className="text-2xs text-brand px-2 font-semibold uppercase tracking-widest">Settings</p>
      <div className="mt-2 space-y-2">
        {groups.map((group) => (
          <section key={group.id} aria-labelledby={`settings-nav-${variant}-${group.id}`}>
            <h2
              id={`settings-nav-${variant}-${group.id}`}
              className="text-2xs text-muted-foreground px-2 font-semibold uppercase tracking-wider"
            >
              {group.label}
            </h2>
            <ul className="mt-1 space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.id}>
                    <NavLink
                      to={item.path}
                      end={item.id === 'settings'}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                          'focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2',
                          isActive
                            ? 'bg-primary/10 text-foreground'
                            : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <Icon
                            className={cn('h-3.5 w-3.5 shrink-0', isActive && 'text-primary')}
                            aria-hidden="true"
                          />
                          {settingsSectionLabel(item)}
                        </>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </nav>
  );
}
